import { useState } from "react";
import { Check, Copy, MessageCircle, Pencil, Phone } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { displayAge } from "@/lib/patientAge";
import { formatDate } from "@/lib/formatDate";
import { cn } from "@/lib/utils";
import { groupPhone } from "./PatientBanner";

export interface DetailsPatient {
  name: string;
  age?: number | null;
  gender?: string | null;
  date_of_birth?: string | null;
  mrn?: string | null;
  phone?: string | null;
  phone_2?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  pin_code?: string | null;
  preferred_language?: string | null;
  aadhar_number?: string | null;
  blood_group?: string | null;
  allergies?: string | null;
  chronic_conditions?: string | null;
  current_medications?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  payment_preference?: string | null;
  created_at?: string | null;
}

interface Props {
  patient: DetailsPatient;
  visitCount: number;
  /** Date of the most recent visit, if any */
  lastVisit?: string | null;
  /** Absent without patients:write: the record is read-only */
  onEdit?: () => void;
}

const notRecorded = <span className="text-muted-foreground">Not recorded</span>;

/** Label on the left, value on the right; an empty value says so instead of disappearing */
function Row({ label, children, actions }: { label: string; children?: React.ReactNode; actions?: React.ReactNode }) {
  const empty = children == null || children === "" || children === false;
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 px-5 py-2.5 min-h-10 items-start">
      <dt className="text-xs text-muted-foreground pt-0.5">{label}</dt>
      <dd className="flex items-start justify-between gap-2 min-w-0 text-sm text-foreground">
        <div className="min-w-0 break-words">{empty ? notRecorded : children}</div>
        {!empty && actions && <div className="flex items-center gap-1 -my-1 flex-shrink-0">{actions}</div>}
      </dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="border-b border-border last:border-0">
      <h3 className="px-5 pt-4 pb-1 text-[15px] font-semibold text-foreground">{title}</h3>
      <dl className="divide-y divide-divider">{children}</dl>
    </section>
  );
}

// Indian mobile numbers: keep the last 10 digits
const tenDigits = (n?: string | null) => (n ?? "").replace(/\D/g, "").slice(-10);

function CallButtons({ number, name, whatsapp = false }: { number: string; name: string; whatsapp?: boolean }) {
  const digits = tenDigits(number);
  if (digits.length !== 10) return null;
  const iconLink = cn(buttonVariants({ variant: "ghost", size: "sm" }), "touch-target w-8 px-0");
  return (
    <>
      <a href={`tel:${digits}`} aria-label={`Call ${name}`} title="Call" className={iconLink}>
        <Phone className="w-4 h-4" />
      </a>
      {whatsapp && (
        <a href={`https://wa.me/91${digits}`} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${name}`} title="WhatsApp" className={iconLink}>
          <MessageCircle className="w-4 h-4" />
        </a>
      )}
    </>
  );
}

/**
 * Everything on the patient's record, for the "Patient details" side panel: clinical background first
 * (what must not be missed), then how to reach them, then the record itself. Empty fields stay visible
 * as "Not recorded" so gaps are obvious.
 */
export default function PatientDetailsPanel({ patient, visitCount, lastVisit, onEdit }: Props) {
  const [copied, setCopied] = useState(false);
  const age = displayAge(typeof patient.age === "string" ? Number(patient.age) || null : patient.age, patient.date_of_birth);
  const meds = (patient.current_medications ?? "").split(/[,;\n]+/).map(m => m.trim()).filter(Boolean);
  const aadhaar = (patient.aadhar_number ?? "").replace(/\D/g, "");

  // What front desk would want to fill in next
  const missing = [
    !patient.allergies && "allergies",
    !patient.address && !patient.city && "address",
    !patient.emergency_contact_name && "emergency contact",
    !patient.date_of_birth && "date of birth",
  ].filter(Boolean) as string[];

  const copyMrn = async () => {
    if (!patient.mrn) return;
    try {
      await navigator.clipboard.writeText(patient.mrn);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — the MRN is still selectable */ }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex-1 overflow-y-auto">
        {missing.length > 0 && (
          <p className="mx-5 mt-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px] text-muted-foreground">
            Not recorded: {missing.join(", ")}.{" "}
            {onEdit && (
              <button type="button" onClick={onEdit} className="touch-target font-medium text-primary hover:underline underline-offset-2">
                Add now
              </button>
            )}
          </p>
        )}

        <Section title="Clinical">
          <Row label="Allergies">
            {patient.allergies && <span className="font-semibold text-destructive">{patient.allergies}</span>}
          </Row>
          <Row label="Long-term conditions">{patient.chronic_conditions}</Row>
          <Row label="Current medicines">
            {meds.length > 0 && (
              <ul className="space-y-0.5">{meds.map((m, i) => <li key={i}>{m}</li>)}</ul>
            )}
          </Row>
          <Row label="Blood group">{patient.blood_group && <span className="font-mono">{patient.blood_group}</span>}</Row>
        </Section>

        <Section title="Contact">
          <Row label="Phone" actions={patient.phone && <CallButtons number={patient.phone} name={patient.name} whatsapp />}>
            {patient.phone && <span className="tabular-nums">{groupPhone(patient.phone)}</span>}
          </Row>
          <Row label="Alternate phone" actions={patient.phone_2 && <CallButtons number={patient.phone_2} name={patient.name} />}>
            {patient.phone_2 && <span className="tabular-nums">{groupPhone(patient.phone_2)}</span>}
          </Row>
          <Row label="Email">
            {patient.email && <a href={`mailto:${patient.email}`} className="text-primary hover:underline underline-offset-2">{patient.email}</a>}
          </Row>
          <Row label="Address">
            {(patient.address || patient.city || patient.pin_code) && (
              <>
                {patient.address && <span className="block whitespace-pre-line">{patient.address}</span>}
                {(patient.city || patient.pin_code) && (
                  <span className={cn("block capitalize", patient.address && "text-muted-foreground")}>
                    {[patient.city, patient.pin_code].filter(Boolean).join(" – ")}
                  </span>
                )}
              </>
            )}
          </Row>
          <Row label="Language">{patient.preferred_language}</Row>
        </Section>

        <Section title="Emergency contact">
          <Row
            label={patient.emergency_contact_name ? "Name" : "Contact"}
            actions={patient.emergency_contact_phone && <CallButtons number={patient.emergency_contact_phone} name={patient.emergency_contact_name || "emergency contact"} />}
          >
            {(patient.emergency_contact_name || patient.emergency_contact_phone) && (
              <>
                {patient.emergency_contact_name && <span className="block capitalize">{patient.emergency_contact_name}</span>}
                {patient.emergency_contact_phone && <span className="block tabular-nums text-muted-foreground">{groupPhone(patient.emergency_contact_phone)}</span>}
              </>
            )}
          </Row>
        </Section>

        <Section title="Record">
          <Row
            label="MRN"
            actions={patient.mrn && (
              <Button variant="ghost" size="sm" onClick={copyMrn} aria-label={copied ? "MRN copied" : "Copy MRN"} className="touch-target w-8 px-0">
                {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
              </Button>
            )}
          >
            {patient.mrn && <span className="font-mono">{patient.mrn}</span>}
          </Row>
          <Row label="Age and sex">{[age, patient.gender].filter(Boolean).join(" · ")}</Row>
          <Row label="Date of birth">{patient.date_of_birth && formatDate(patient.date_of_birth)}</Row>
          <Row label="Visits">
            {visitCount > 0
              ? <>{visitCount}{lastVisit && <span className="text-muted-foreground"> · last on {formatDate(lastVisit)}</span>}</>
              : <span className="text-muted-foreground">No visits yet</span>}
          </Row>
          <Row label="Registered">{patient.created_at && formatDate(patient.created_at)}</Row>
          <Row label="Aadhaar">{aadhaar.length >= 4 && <span className="font-mono">•••• •••• {aadhaar.slice(-4)}</span>}</Row>
          <Row label="Payment">{patient.payment_preference}</Row>
        </Section>
      </div>

      {onEdit && (
        <div className="flex-shrink-0 flex justify-end px-5 py-3 border-t border-border bg-card">
          <Button size="sm" onClick={onEdit} className="gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Edit patient
          </Button>
        </div>
      )}
    </div>
  );
}
